import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { EnvelopeOpenIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import api from '@/services/api';
import './AuthPages.css';

interface ValidationError {
  field: string;
  message: string;
}

interface VerifyOTPResponse {
  status: string;
  message: string;
  data?: {
    reset_password_token?: string;
  };
}

const VerifyOTPPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [apiMessage, setApiMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(359); // 5 minutes 59 seconds
  const [fade, setFade] = useState(false);
  const [askWait, setAskWait] = useState(false);
  const [canResend, setCanResend] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { email, reset_password_token } = location.state || {};

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
      return;
    }

    setFade(true);
    const root = document.querySelector('#root');
    root?.classList.add('auth-background');

    return () => {
      root?.classList.remove('auth-background');
    };
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Auto focus next
    if (element.value && index < 5) {
      const nextInput = element.nextElementSibling as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = e.currentTarget.previousElementSibling as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    if (otp.join('').length !== 6) {
      newErrors.push({ field: 'otp', message: 'Please enter complete 6-digit code' });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage('');
    setErrors([]);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/verify-otp', {
        email,
        otp: otp.join(''),
        reset_password_token // send true if no reset_password_token to get a new one,
      });
      
      const result: VerifyOTPResponse = response.data as VerifyOTPResponse;

      // console.log('OTP verification response:', result);

      if (result.status === "success") {
        setApiMessage(result.message || 'OTP verified successfully!');
        
        setTimeout(() => {
          setFade(false);
          setTimeout(() => {
            navigate('/reset-password', { 
              state: { 
                email,
                reset_password_token: result.data?.reset_password_token
              } 
            });
          }, 300);
        }, 1500);
      } else {
        setApiMessage(result.message || 'Invalid verification code.');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        setApiMessage(errorData.message || 'Failed to verify OTP.');
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) {
      let miliLeft = timeLeft * 1000;
      let toastClose = miliLeft < 5000 ? miliLeft : 5000;
      
      console.log('Time left to resend OTP:', timeLeft);

      setAskWait(true);
      setTimeout(() => {
        setAskWait(false);
      }, 3000);

      toast.info(`Please wait before resending OTP. You can resend the code once the timer expires in ${formatTime(timeLeft)}.`, {
              position: "top-right",
              autoClose: toastClose,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
      return;
    }

    setIsLoading(true);
    setApiMessage('');

    try {     
      const response = await api.post('/auth/forgot-password', { email });
      const result = response.data as VerifyOTPResponse;

      if (result.status === "success") {
        setApiMessage('New verification code sent!');
        setTimeLeft(359);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        
        // Focus first input
        const firstInput = document.getElementById('otp-0') as HTMLInputElement;
        if (firstInput) firstInput.focus();
      }
    } catch (error: any) {
      setApiMessage('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`auth-container min-w-full flex-col ${fade ? 'fade-in' : 'fade-out'}`}>
      <h1 className="auth-title mb-8">OTP Verification</h1>
      
      {apiMessage && (
        <div className={`message ${apiMessage.includes('success') ? 'message-success' : 'message-error'}`}>
          <div className="message-content">
            {apiMessage}
          </div>
        </div>
      )}

      <div className="auth-card min-w-full max-w-fit">
        <div className="flex items-center justify-center mb-4">
            <EnvelopeOpenIcon className="auth-icon h-32 drop-shadow-[0_5px_5px_rgba(56,136,255,1)]" />
        </div>
        <p className="auth-subtitle font-normal">
          A 6-digit code has been sent to <br />
          <span className="font-semibold">{email}</span>
        </p>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <div className="otp-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  className="otp-input rounded-sm bg-blue-950/80 text-center"
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={(e) => e.target.select()}
                  disabled={isLoading}
                />
              ))}
            </div>
            {errors.find(error => error.field === 'otp') && (
              <div className="error-text text-center mt-2">
                {errors.find(error => error.field === 'otp')?.message}
              </div>
            )}
            
            <div className="otp-info">
              <p
                  className={`${canResend ? '' : 'animate-pulse'}`}>The OTP will be expired in <span className={`${askWait ? 'text-orange-500 text-lg font-bold' : ''}`}>{formatTime(timeLeft)}</span></p>
              <p>
                Didn't receive the code?{' '}
                <button
                  type="button"
                  className={`resend-button ${canResend ? 'text-orange-500 hover:text-orange-400' : 'text-gray-400 no-underline'}`}
                  onClick={handleResendOTP}
                >
                  Resend
                </button>
              </p>
            </div>
          </div>
          
          <button 
            type="submit" 
            className={`auth-button rounded-full ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>

          <div className="auth-link">
            <span>Remember your password? </span>
            <Link to="/login" className="auth-link-text">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTPPage;